import { useEffect, useState } from "react";

const options = [
  "I'm not sure what treatment I need",
  "I want to understand pricing",
  "I'm worried about pain",
  "I want to know treatment time",
  "I want to book a consultation",
  "Other question",
];

type Props = {
  open: boolean;
  onClose: () => void;
};

export default function ConsultationDrawer({ open, onClose }: Props) {
  const [selected, setSelected] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [status, setStatus] = useState<"idle" | "sent">("idle");

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("sent");
    setTimeout(() => {
      onClose();
      setStatus("idle");
      setSelected(null);
      setMessage("");
      setName("");
      setContact("");
    }, 1600);
  };

  return (
    <div
      className={`fixed inset-0 z-[80] transition-opacity ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      aria-hidden={!open}
    >
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <aside
        className={`absolute right-0 top-0 h-full w-full sm:w-[480px] bg-white flex flex-col transition-transform duration-500 ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div>
            <div className="text-[11px] tracking-[0.2em] text-ink-muted uppercase">Aline Dental</div>
            <div className="text-lg font-medium">Online Consultation</div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-6" data-readdy-form>
          <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />

          <div>
            <div className="text-sm text-ink-muted mb-3">What can we help with?</div>
            <div className="flex flex-col gap-2">
              {options.map((opt) => (
                <button
                  type="button"
                  key={opt}
                  onClick={() => setSelected(opt)}
                  className={`text-left px-4 py-3 rounded-md border text-sm transition whitespace-nowrap cursor-pointer ${
                    selected === opt
                      ? "border-brand bg-brand-soft text-ink"
                      : "border-gray-200 hover:border-gray-300 text-ink"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm text-ink-muted mb-2 block">Tell us a little more</label>
            <textarea
              value={message}
              name="message"
              onChange={(e) => setMessage(e.target.value.slice(0, 500))}
              rows={4}
              maxLength={500}
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand resize-none"
              placeholder="Share as much or as little as you like."
            />
            <div className="text-[11px] text-ink-faint mt-1 text-right">{message.length}/500</div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              name="name"
              placeholder="Your name"
              className="border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand"
              required
            />
            <input
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              name="email"
              type="email"
              placeholder="Email"
              className="border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand"
              required
            />
          </div>

          <div className="mt-auto pt-2">
            <button
              type="submit"
              className="w-full py-3 rounded-md bg-brand text-white text-sm font-medium hover:bg-[#027ea3] transition whitespace-nowrap cursor-pointer"
            >
              {status === "sent" ? "Received — we'll be in touch" : "Send to the clinic"}
            </button>
            <p className="text-[11px] text-ink-faint mt-3 text-center">
              Replies within one working day.
            </p>
          </div>
        </form>
      </aside>
    </div>
  );
}